// Multi-select filter button with a searchable checkbox popover.
// Used for the Dashboard client filter and the Monitoring SIP / server /
// template filters.
//
//   <MultiPicker label="SIPs" icon={Phone} options={[{ value, label, sub }]}
//       selected={['9001']} onChange={vals => …} groupInput align="right" />
//
// selected = [] means "all". With groupInput, a text box accepts a group of
// values such as "9001-9010, 9020" (ranges expand) and selects those.
import { useState, useEffect, useRef, useMemo } from 'react'
import { ChevronDown, Search, X, Check } from 'lucide-react'

// "9001-9010, 9020" → ['9001', …, '9010', '9020'] (capped like the API's).
export function expandGroup(text) {
    const out = []
    for (const part of String(text || '').split(/[,\s]+/)) {
        const m = part.match(/^(\d+)-(\d+)$/)
        if (m) {
            const [a, b] = [+m[1], +m[2]].sort((x, y) => x - y)
            for (let n = a; n <= b && out.length < 2000; n++) out.push(String(n))
        } else if (part) out.push(part)
    }
    return out
}

export default function MultiPicker({ label, icon: Icon, options, selected, onChange, groupInput = false, allLabel = 'All', width = 280, align = 'left' }) {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const [group, setGroup] = useState('')
    const ref = useRef(null)

    useEffect(() => {
        if (!open) return
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [open])

    const sel = useMemo(() => new Set(selected.map(String)), [selected])
    const shown = useMemo(() => {
        if (!q) return options
        const s = q.toLowerCase()
        return options.filter(o => `${o.label} ${o.sub || ''}`.toLowerCase().includes(s))
    }, [options, q])

    const toggle = (v) => {
        const n = new Set(sel)
        n.has(String(v)) ? n.delete(String(v)) : n.add(String(v))
        onChange([...n])
    }
    const applyGroup = () => {
        const known = new Set(options.map(o => String(o.value)))
        const vals = expandGroup(group).filter(v => !known.size || known.has(v))
        onChange(vals)
        setGroup('')
    }

    const active = selected.length > 0
    const summary = !active ? allLabel
        : selected.length === 1 ? (options.find(o => String(o.value) === String(selected[0]))?.label ?? selected[0])
        : `${selected.length} selected`

    return (
        <div ref={ref} className="tw-relative">
            <button type="button" onClick={() => setOpen(o => !o)}
                className={[
                    'tw-inline-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-px-2.5 tw-text-xs tw-font-medium',
                    active ? 'tw-border-sky-500/60 tw-bg-sky-500/10 tw-text-sky-400' : 'tw-border-border tw-bg-card tw-text-fg'
                ].join(' ')}>
                {Icon && <Icon size={13} />}
                <span className="tw-text-muted">{label}:</span>
                <span className="tw-max-w-[140px] tw-truncate">{summary}</span>
                {active
                    ? <X size={13} className="tw-opacity-70 hover:tw-opacity-100" onClick={(e) => { e.stopPropagation(); onChange([]) }} />
                    : <ChevronDown size={13} className="tw-opacity-60" />}
            </button>

            {open && (
                <div className={`tw-absolute ${align === 'right' ? 'tw-right-0' : 'tw-left-0'} tw-top-[calc(100%+4px)] tw-z-50 tw-flex tw-max-h-[380px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-shadow-xl`}
                    style={{ width }}>
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-2.5 tw-py-2">
                        <Search size={13} className="tw-text-muted" />
                        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
                            className="tw-w-full tw-border-0 tw-bg-transparent tw-text-xs tw-text-fg tw-outline-none" />
                    </div>
                    {groupInput && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-2.5 tw-py-2">
                            <input value={group} onChange={e => setGroup(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyGroup()}
                                placeholder="Group, e.g. 9001-9010, 9020"
                                className="tw-h-7 tw-w-full tw-rounded tw-border tw-border-solid tw-border-border tw-bg-input tw-px-2 tw-font-mono tw-text-xs tw-text-fg" />
                            <button type="button" className="btn btn-secondary btn-sm" disabled={!group.trim()} onClick={applyGroup}>Select</button>
                        </div>
                    )}
                    <div className="tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5 tw-text-[11px] tw-text-muted">
                        <span>{active ? `${selected.length} of ${options.length} selected` : `${options.length} — none selected = all`}</span>
                        <span className="tw-flex tw-gap-2">
                            <button type="button" className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-sky-400 hover:tw-underline"
                                onClick={() => onChange([...new Set([...selected.map(String), ...shown.map(o => String(o.value))])])}>
                                Select {q ? 'shown' : 'all'}
                            </button>
                            {active && (
                                <button type="button" className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-muted hover:tw-text-fg"
                                    onClick={() => onChange([])}>Clear</button>
                            )}
                        </span>
                    </div>
                    <div className="tw-overflow-y-auto tw-pb-1">
                        {shown.length === 0 && <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-muted">No matches</div>}
                        {shown.map(o => {
                            const on = sel.has(String(o.value))
                            return (
                                <div key={o.value} onClick={() => toggle(o.value)}
                                    className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-text-xs hover:tw-bg-card-hover ${on ? 'tw-text-fg' : 'tw-text-fg-secondary'}`}>
                                    <span className={`tw-inline-flex tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-solid ${on ? 'tw-border-sky-500 tw-bg-sky-500 tw-text-white' : 'tw-border-border'}`}>
                                        {on && <Check size={10} strokeWidth={3} />}
                                    </span>
                                    <span className="tw-truncate tw-font-medium">{o.label}</span>
                                    {o.sub && <span className="tw-ml-auto tw-shrink-0 tw-truncate tw-text-[11px] tw-text-muted">{o.sub}</span>}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
